import { Box, useMediaQuery } from "@mui/system";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { settings } from "app";
import permissions from "app/permissions";
import { studentRequests } from "api/studentApi";
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  Button,
  Datagrid,
  FunctionField,
  List,
  NumberField,
  NumberInput,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  SimpleList,
  TextField,
  TextInput,
  TopToolbar,
  useRecordContext,
} from "react-admin";
import { CourseChoices, NationalityType } from "utils/choices";
import { EducationType, MOBILE_BREAKPOINT, STUDENTS } from "utils/constants";
import { EmptyList } from "server/components";
import { MobileListRow } from "features/MobileListRow";
import { getDisplayName } from "utils/helpers";
import { Link } from "react-router-dom";
import DeclarationsDialog from "./Declarations/DeclarationsDialog";
import SecuredComponent from "server/security/SecuredComponent";
import { UsersExporter } from "../XlsxExporter";
import { ExportXlsButton } from "features/ExportXlsxButton";
import { tenants } from "utils/tenants";
import MoveToGroupDialog from "./MoveToGroup/MoveToGroupFormDialog";
import ExchangeGroupDialog from "./ExchangeGroup/ExchangeGroupDialog";
import { accountRequests } from "api/accountApi";
import { administrationRequests } from "api/administrationApi";
import CustomDateInput from "server/components/Fields/CustomDateInput";

const studentFilters = [
  <TextInput
    label="Име"
    source="firstName"
    style={{ width: 200 }}
    alwaysOn
  />,
  <TextInput
    label="Фамилия"
    source="lastName"
    style={{ width: 200 }}
    alwaysOn
  />,
  <TextInput
    label="ЕГН"
    source="personId"
    style={{ width: 200 }}
    alwaysOn
  />,
  <TextInput
    label="Факултетен номер"
    source="facultyNumber"
    style={{ width: 200 }}
    alwaysOn
  />,
  <ReferenceInput
    label="Специалност"
    source="specialityId"
    reference={administrationRequests.getSpecialitiesSelect()}
    filter={{ filterByUser: true }}
    style={{ width: 415 }}
    alwaysOn
  >
    <AutocompleteInput
      label="Специалност"
      style={{ width: 415 }}
      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
    />
  </ReferenceInput>,
  ...(settings.tenant === tenants.MGU
    ? [
        <ReferenceInput
          label="Факултет"
          source="structuralUnitId"
          reference={administrationRequests.getStructuralUnitsSelect()}
          filter={{ filterByUser: true, onlyProvidingCampaign: true }}
          style={{ width: 415 }}
          alwaysOn
        >
          <AutocompleteInput
            label="Факултет"
            style={{ width: 415 }}
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
          />
        </ReferenceInput>,
      ]
    : []),
  <SelectInput
    source="course"
    label="Курс"
    choices={CourseChoices}
    alwaysOn
    emptyText={"Всички"}
    style={{ width: 200 }}
  />,
  ...(settings.tenant === tenants.MU
    ? [
        <ReferenceInput
          source="instructionLanguageId"
          reference={administrationRequests.getInstructionLanguagesSelect()}
          style={{ width: 200 }}
          alwaysOn
        >
          <SelectInput
            label="Език на обучение"
            emptyText={"Всички"}
            style={{ width: 200 }}
          />
        </ReferenceInput>,
        <SelectInput
          alwaysOn
          choices={NationalityType}
          source="nationalityType"
          label="Националност"
          style={{ width: 200 }}
        />,
      ]
    : []),
  <NumberInput
    label="Учебна група"
    source="studyGroup"
    onWheel={e => e.target.blur()}
    style={{ width: 200 }}
    alwaysOn
  />,
  <ReferenceArrayInput
    reference={studentRequests.getEducationStatusesSelect()}
    source="educationStatusIds"
    style={{ width: 200 }}
    alwaysOn
  >
    <AutocompleteArrayInput
      label="Статус"
      optionText="name"
      style={{ width: 200 }}
    />
  </ReferenceArrayInput>,
  <ReferenceInput
    source="studyTypeId"
    reference={administrationRequests.getStudyTypesSelect()}
    label="Вид на обучение"
    style={{ width: 200 }}
    alwaysOn
  >
    <SelectInput
      optionText="name"
      label="Вид на обучение"
      emptyText={false}
      style={{ width: 200 }}
    />
  </ReferenceInput>,
  ...(settings.tenant === tenants.MU
    ? [
        <CustomDateInput
          style={{ width: 200 }}
          noTime
          source="dateOfBirth"
          label="Дата на раждане"
          alwaysOn
        />,
        <ReferenceInput
          label="Държава на раждане"
          source="birthCountryId"
          reference={administrationRequests.getCountriesSelect()}
          style={{ width: 200 }}
          alwaysOn
        >
          <AutocompleteInput
            label="Държава на раждане"
            optionText="nameBg"
            style={{ width: 200 }}
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
          />
        </ReferenceInput>,
        <ReferenceInput
          label="Гражданство"
          source="primaryCitizenshipCountryId"
          reference={administrationRequests.getCountriesSelect()}
          style={{ width: 200 }}
          alwaysOn
        >
          <AutocompleteInput
            label="Гражданство"
            optionText="nameBg"
            style={{ width: 200 }}
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
          />
        </ReferenceInput>,
      ]
    : []),
];

const DetailsButton = ({ studentEducationId, label }) => {
  const record = useRecordContext();
  studentEducationId = record?.studentEducationId;

  return (
    <Button
      component={Link}
      to={`${studentEducationId}/show`}
      label={label}
    >
      <VisibilityIcon />
    </Button>
  );
};

const ListStudents = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const renderActionButtons = record => (
    <Box sx={settings.style.wrappingActionButtons}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "start", gap: 1 }}>
        <SecuredComponent permission={permissions.canMoveStudentsBetweenStudyGroups}>
          <MoveToGroupDialog />
          <ExchangeGroupDialog />
        </SecuredComponent>
      </Box>
      <SecuredComponent permission={permissions.canPreviewStudentDeclarations}>
        <DeclarationsDialog />
      </SecuredComponent>
      <SecuredComponent permission={permissions.hasShowStudentDetailsPermisson}>
        <DetailsButton label="Детайли" />
      </SecuredComponent>
    </Box>
  );

  const ListActions = () => {
    return (
      <TopToolbar>
        <ExportXlsButton exporter={UsersExporter(STUDENTS)} />
      </TopToolbar>
    );
  };

  return (
    <List
      actions={<ListActions />}
      title="Студенти"
      filter={{ EducationType: EducationType.Regular }}
      filters={studentFilters}
      sx={settings.style.expandStyle}
    >
      {isMobile ? (
        <SimpleList
          empty={<EmptyList />}
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <MobileListRow
              fields={[
                { label: "Факултетен №", value: record.facultyNumber },
                { label: "Курс", value: record.course },
              ]}
            />
          )}
          secondaryText={record => (
            <div>
              <MobileListRow
                fields={[
                  {
                    label: "Име",
                    value: (
                      <ReferenceField
                        label="Име"
                        source="userId"
                        reference={accountRequests.getUsersSelect()}
                        sortable={false}
                      >
                        <TextField source="firstName" />
                      </ReferenceField>
                    ),
                  },
                  {
                    label: "Презиме",
                    value: (
                      <ReferenceField
                        label="Презиме"
                        source="userId"
                        reference={accountRequests.getUsersSelect()}
                        sortable={false}
                      >
                        <TextField source="secondName" />
                      </ReferenceField>
                    ),
                  },
                  {
                    label: "Фамилия",
                    value: (
                      <ReferenceField
                        label="Фамилия"
                        source="userId"
                        reference={accountRequests.getUsersSelect()}
                        sortable={false}
                      >
                        <TextField source="lastName" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Специалност",
                    value: (
                      <ReferenceField
                        label="Специалност"
                        source="specialityId"
                        reference={administrationRequests.getSpecialitiesSelect()}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              {settings.tenant === tenants.MU && (
                <MobileListRow
                  fields={[
                    {
                      label: "Език на обучение",
                      value: (
                        <ReferenceField
                          source="instructionLanguageId"
                          reference={administrationRequests.getInstructionLanguagesSelect()}
                          label="Език на обучение"
                        >
                          <TextField source="name" />
                        </ReferenceField>
                      ),
                    },
                  ]}
                />
              )}
              <MobileListRow
                fields={[
                  {
                    label: "Вид на обучение",
                    value: (
                      <ReferenceField
                        source="studyTypeId"
                        reference={administrationRequests.getStudyTypesSelect()}
                        label="Вид на обучение"
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow fields={[{ label: "Учебна група", value: record.studyGroup }]} />
              <MobileListRow fields={[{ label: "Статус", value: record?.educationStatusNameBg || "-" }]} />
              {renderActionButtons(record, "", "", "")}
            </div>
          )}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
          sx={{ ...settings.style.firstExpandIconDatagrid, ...settings.style.compactDatagridCells }}
        >
          {/* One "Имена" column instead of three - saves width so the table fits a 14" screen. */}
          <ReferenceField
            label="Имена"
            source="userId"
            reference={accountRequests.getUsersSelect()}
            sortable={false}
          >
            <FunctionField render={getDisplayName} />
          </ReferenceField>
          <TextField
            source="facultyNumber"
            label="Факултетен №"
          />
          <ReferenceField
            label="Специалност"
            source="specialityId"
            reference={administrationRequests.getSpecialitiesSelect()}
          >
            <TextField source="name" />
          </ReferenceField>
          <TextField
            source="course"
            label="Курс"
            sortable={false}
          />
          {settings.tenant === tenants.MU && (
            <ReferenceField
              source="instructionLanguageId"
              reference={administrationRequests.getInstructionLanguagesSelect()}
              label="Език на обучение"
            >
              <TextField source="name" />
            </ReferenceField>
          )}
          <ReferenceField
            source="studyTypeId"
            reference={administrationRequests.getStudyTypesSelect()}
            label="Вид на обучение"
          >
            <TextField source="name" />
          </ReferenceField>
          <NumberField
            label="Учебна група"
            source="studyGroup"
            textAlign="left"
            emptyText="-"
          />
          <FunctionField
            label="Статус"
            render={record => record?.educationStatusNameBg || "-"}
          />
          <FunctionField render={record => renderActionButtons(record)} />
        </Datagrid>
      )}
    </List>
  );
};

export default ListStudents;
